import React, { createContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { loginUser, registerUser, verifyOtp, logoutUser, googleSignIn } from '../api/auth';
import { getToken, setToken as persistToken, removeToken } from '../utils/tokenStorage';
import { syncChatSession } from '../api/chatbot';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(getToken('token') || null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Check for token and set auth state on mount
    useEffect(() => {
        const checkAuth = async () => {
            const storedToken = getToken('token');
            if (storedToken) {
                try {
                    const decoded = jwtDecode(storedToken);
                    // Check if token is expired
                    if (decoded.exp * 1000 < Date.now()) {
                        handleLogout();
                        return;
                    }

                    setCurrentUser({
                        id: decoded.id,
                        email: decoded.email,
                        name: decoded.name,
                        profilePicPath: decoded.profilePicPath,
                    });
                    setUserRole(decoded.role);
                    setToken(storedToken);
                    setIsAuthenticated(true);
                } catch (error) {
                    console.error('Invalid token:', error);
                    handleLogout();
                }
            }
            else{
                handleLogout();
            }

            setLoading(false);
        };

        checkAuth();
    }, []);

    const handleLogin = async (email, password, role, rememberMe = false) => {
        try {
            setLoading(true);
            const response = await loginUser(email, password, role);
            if (response?.responseData?.token && !response?.responseData?.requiresVerification) {
                persistToken('token', response.responseData.token, rememberMe);
                const decoded = jwtDecode(response.responseData.token);

                setCurrentUser({
                    id: decoded.id,
                    email: decoded.email,
                    name: decoded.name,
                    profilePicPath: decoded.profilePicPath,
                });
                setUserRole(decoded.role);
                setToken(response.token);
                setIsAuthenticated(true);
                // Resolve this user's canonical chat conversation so the
                // assistant resumes the same thread on any device (non-blocking).
                syncChatSession();
                /*
                 * selfParked means this member paused their OWN account and has
                 * just signed in with it. The server lets them through on
                 * purpose — it is the only route back — and the caller offers
                 * reactivation. Everything else they try is still refused until
                 * they take it.
                 */
                return { success: true, selfParked: Boolean(response.responseData.selfParked) };
            }
            else if(response?.responseData?.token && response?.responseData?.requiresVerification){
                localStorage.setItem('tempToken', response.responseData.token)
                return response;
            }

            return { success: false, error: response?.responseMessage || 'Invalid credentials' };
        } catch (error) {
            return { success: false, error: error.response?.data?.responseMessage || error.response?.data?.message || 'Login failed' };
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async (credential, role, rememberMe = false, referralCode = '') => {
        try {
            setLoading(true);
            const response = await googleSignIn(credential, role, referralCode);
            if (response?.responseData?.token) {
                persistToken('token', response.responseData.token, rememberMe);
                const decoded = jwtDecode(response.responseData.token);

                setCurrentUser({
                    id: decoded.id,
                    email: decoded.email,
                    name: decoded.name,
                    profilePicPath: decoded.profilePicPath,
                });
                setUserRole(decoded.role);
                setToken(response.responseData.token);
                setIsAuthenticated(true);
                syncChatSession();
                return { success: true, selfParked: Boolean(response.responseData.selfParked) };
            }

            return { success: false, error: response?.responseMessage || 'Google login failed' };
        } catch (error) {
            return { success: false, error: error.response?.data?.responseMessage || error.response?.data?.message || 'Google login failed' };
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (userData, role) => {
        try {
            setLoading(true);
            const response = await registerUser(userData, role);
            if (response.responseData.token) {
                localStorage.setItem('tempToken', response.responseData.token)
                return response;
            }

            return { success: false, error: response || 'Registration failed' };
        } catch (error) {
            return { success: false, error: error.response?.data || 'Registration failed' };
        } finally {
            setLoading(false);
        }
    };

    const handleOtpVerification = async (data) => {
        const { rememberMe = false, ...otpData } = data;
        try {
            setLoading(true);
            const response = await verifyOtp(otpData);

            if (response.responseData?.type !== 'signUp' && response.responseData.token) {
                persistToken('token', response.responseData.token, rememberMe);
                const decoded = jwtDecode(response.responseData.token);

                setCurrentUser({
                    id: decoded.id,
                    email: decoded.email,
                    name: decoded.name,
                    profilePicPath: decoded.profilePicPath,
                });
                setUserRole(decoded.role);
                setToken(response.responseData.token);
                setIsAuthenticated(true);
                return response;
            }
            else if(response.responseData?.type === 'signUp'){
                return response;
            }

            return { success: false, error: 'OTP verification failed' };
        } catch (error) {
            return { success: false, error: error.response?.data?.message || 'OTP verification failed' };
        } finally {
            setLoading(false);
        }
    };

    // Applies a JWT issued by the chat-based registration/login flows —
    // flips auth state in place with no page reload or navigation, exactly
    // like a traditional login. Returns true on success so callers can
    // distinguish a bad/expired token. (Used by AssistantOverlay.)
    const applyChatAuthToken = (jwtToken) => {
        try {
            const decoded = jwtDecode(jwtToken);
            persistToken('token', jwtToken);
            setCurrentUser({
                id: decoded.id,
                email: decoded.email,
                name: decoded.name,
                profilePicPath: decoded.profilePicPath,
            });
            setUserRole(decoded.role);
            setToken(jwtToken);
            setIsAuthenticated(true);
            return true;
        } catch (error) {
            console.error('Invalid chat auth token:', error);
            return false;
        }
    };

    const handleLogout = async () => {
        
        // try {
        //     await logoutUser();
        // } catch (error) {
        //     console.error('Logout error:', error);
        // } finally {
        //     // Clear local storage and state regardless of API call result
        //     localStorage.removeItem('token');
        //     setCurrentUser(null);
        setUserRole(null);
        //     setToken(null);
        setIsAuthenticated(false);
        removeToken('token');
        localStorage.removeItem('tempToken')
        //     toast.info('Logged out successfully');
        // }
    };

    const authContextValue = {
        currentUser,
        userRole,
        isAuthenticated,
        loading,
        token,
        login: handleLogin,
        loginWithGoogle: handleGoogleLogin,
        register: handleRegister,
        verifyOtp: handleOtpVerification,
        logout: handleLogout,
        applyChatAuthToken,
    };

    return (
        <AuthContext.Provider value={authContextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;